import { Cryptor } from "@cryptors/api-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { HttpError, IResourceComponentsProps } from "@refinedev/core";
import { Create } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { usePostCryptor } from "@/api/cryptor.api";
import UploadField from "@/components/forms/UploadField";
import { removeQueryParams } from "@/utils/parseUrl";

import CryptorAddressesField from "./components/CryptorAddressesField";
import CryptorBirthDateField from "./components/CryptorBirthDateField";
import CryptorBirthDateRawField from "./components/CryptorBirthDateRawField";
import CryptorDeathDateField from "./components/CryptorDeathDateField";
import CryptorDescriptionAIField from "./components/CryptorDescriptionAI";
import CryptorDescriptionField from "./components/CryptorDescriptionField";
import CryptorDifficultyField from "./components/CryptorDifficultyField";
import CryptorLinkWithThePlaceField from "./components/CryptorLinkWithThePlaceField";
import CryptorMistralAgentIdField from "./components/CryptorMistralAgentIdField";
import CryptorNameTextField from "./components/CryptorNameTextField";
import CryptorPlaceOfBirthField from "./components/CryptorPlaceOfBirthField";
import CryptorPrimaryColorField from "./components/CryptorPrimaryColorField";
import CryptorQuoteField from "./components/CryptorQuoteField";
import CryptorRewardField from "./components/CryptorRewardField";
import CryptorShouldAnimateEyesField from "./components/CryptorShouldAnimateEyesField";
import CryptorTitleField from "./components/CryptorTitleField";
import SecondaryColorField from "./components/SecondaryColorField";
import TimeRestrictionField from "./components/TimeRestrictionField";
import UnderMaintenanceField from "./components/UnderMaintenanceField";
import { CryptorFormData, CryptorSchema } from "./utils/cryptor.schema";

const toDateString = (value: Date | string | null | undefined) =>
  value instanceof Date ? value.toISOString() : (value ?? null);

export const CryptorCreate: React.FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const { mutateAsync: postCryptor } = usePostCryptor();

  const methods = useForm<Cryptor, HttpError, CryptorFormData>({
    resolver: zodResolver(CryptorSchema),
    mode: "onChange",
  });

  const {
    control,
    formState: { isValid, isDirty },
    setError,
    handleSubmit,
    reset,
  } = methods;

  useEffect(() => {
    reset({
      addresses: [],
      draft: true,
      archived: false,
      timeRestriction: false,
      underMaintenance: false,
      mapIcon: { url: null },
    });
  }, [reset]);

  const onFinishHandler = async (data: CryptorFormData) => {
    await postCryptor({
      name: data.name,
      reward: data.reward,
      primaryColor: data.primaryColor,
      secondaryColor: data.secondaryColor,
      timeRestriction: data.timeRestriction,
      timeRestrictionUrl: data.timeRestrictionUrl || null,
      underMaintenance: data.underMaintenance,
      image: { url: removeQueryParams(data.image.url) },
      mapIcon: data.mapIcon?.url
        ? { url: removeQueryParams(data.mapIcon.url) }
        : null,
      obfuscatedImage: { url: removeQueryParams(data.obfuscatedImage.url) },
      avatar: { image: { url: removeQueryParams(data.avatar.image.url) } },
      characterImage: { url: removeQueryParams(data.characterImage.url) },
      addresses: data.addresses.map((address) => ({
        locationAddress: address.locationAddress,
        cityId: address.cityId,
        location: {
          latitude: address.location ? Number(address.location.latitude) : 0,
          longitude: address.location ? Number(address.location.longitude) : 0,
        },
        locationRadius: address.locationRadius
          ? Number(address.locationRadius)
          : 0,
      })),
      descriptionForAI: data.descriptionForAI || null,
      birthDate: toDateString(data.birthDate),
      birthDateRaw: data.birthDateRaw || null,
      deathDate: toDateString(data.deathDate),
      placeOfBirth: data.placeOfBirth,
      translationTitle: { value: data.title },
      translationLinkWithThePlace: { value: data.linkWithThePlace },
      translationDescription: { value: data.description },
      translationQuote: { value: data.quote },
      translationDifficulty: { value: data.difficulty },
      mistralAIAgentIdFR: data.mistralAIAgentIdFR || null,
      mistralAIAgentIdEN: data.mistralAIAgentIdEN || null,
      mistralAIAgentIdES: data.mistralAIAgentIdES || null,
      mistralAIAgentIdDE: data.mistralAIAgentIdDE || null,
      mistralAIAgentIdHU: data.mistralAIAgentIdHU || null,
      mistralAIAgentIdIT: data.mistralAIAgentIdIT || null,
      mistralAIAgentIdNL: data.mistralAIAgentIdNL || null,
      mistralAIAgentIdPL: data.mistralAIAgentIdPL || null,
      mistralAIAgentIdPT: data.mistralAIAgentIdPT || null,
      shouldAnimateEyes: !!data.shouldAnimateEyes,
    });
    navigate("/cryptors");
  };

  return (
    <Create
      saveButtonProps={{
        onClick: handleSubmit(onFinishHandler),
        disabled: !isDirty || !isValid,
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          sx={{ display: "flex", flexDirection: "column" }}
          autoComplete="off"
        >
          <CryptorNameTextField control={control} />
          <Box display="flex" flexDirection="row" gap={2}>
            {!methods.watch("birthDateRaw") && (
              <CryptorBirthDateField control={control} />
            )}
            {!methods.watch("birthDate") && (
              <CryptorBirthDateRawField control={control} />
            )}
            <CryptorDeathDateField control={control} />
            <CryptorPlaceOfBirthField control={control} />
          </Box>
          <CryptorTitleField control={control} />
          <CryptorLinkWithThePlaceField control={control} />
          <CryptorQuoteField control={control} />
          <CryptorDescriptionField control={control} />
          <CryptorDifficultyField control={control} />
          <CryptorRewardField control={control} />
          <CryptorPrimaryColorField control={control} />
          <SecondaryColorField control={control} />
          <TimeRestrictionField control={control} />
          <UnderMaintenanceField control={control} />
          <CryptorAddressesField />
          <UploadField
            name="image.url"
            uploadButtonLabel="Upload image"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
          />
          <UploadField
            name="mapIcon.url"
            uploadButtonLabel="Upload icône carte"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
            optional
          />
          <UploadField
            name="obfuscatedImage.url"
            uploadButtonLabel="Upload image (dissimulée)"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
          />
          <UploadField
            name="avatar.image.url"
            uploadButtonLabel="Upload avatar"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
          />
          <UploadField
            name="characterImage.url"
            uploadButtonLabel="Upload image personnage"
            s3FoldersType={"cryptors"}
            control={control}
            setError={setError}
          />
          <CryptorDescriptionAIField control={control} />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdFR"
            label="Mistral Agent ID FR"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdEN"
            label="Mistral Agent ID EN"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdES"
            label="Mistral Agent ID ES"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdDE"
            label="Mistral Agent ID DE"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdHU"
            label="Mistral Agent ID HU"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdIT"
            label="Mistral Agent ID IT"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdNL"
            label="Mistral Agent ID NL"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdPL"
            label="Mistral Agent ID PL"
          />
          <CryptorMistralAgentIdField
            control={control}
            registerKey="mistralAIAgentIdPT"
            label="Mistral Agent ID PT"
          />
          <CryptorShouldAnimateEyesField control={control} />
        </Box>
      </FormProvider>
    </Create>
  );
};
